import { zodResolver } from "@hookform/resolvers/zod";
import {
  Assignment as AssignmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { type Dayjs } from "dayjs";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  type FieldErrors,
  useForm,
  useWatch,
} from "react-hook-form";

import useQueryClient from "../../../api/query";
import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import {
  type InsuranceVerificationFormFields,
  insuranceVerificationFormSchema,
} from "../form/validation.zod";
import { InsuranceVerificationDataService } from "../services/dataTransformationService";
import {
  type AppliedRuleDisplay,
  formatRuleCondition,
  formatRuleValue,
  type RuleCondition,
  ruleFieldLabel,
  ruleFieldVisibilityKey,
  ruleValueMatches,
} from "../utils/agentRules";
import { AutoCalculationManager } from "../utils/autoCalculation";
import { ConditionalDisplayManager } from "../utils/conditionalDisplay";
import {
  FieldDependencyManager,
  type FieldValue,
} from "../utils/fieldDependencyManager";
import { FieldConverter } from "../utils/fieldUtils";

interface VerificationFormProps {
  verification: InsuranceVerificationMessage;
  onPredictedOutcomeChange?: (outcome: string) => void;
  onFormDataChange?: (data: InsuranceVerificationDetailUpdate) => void;
  onValidationChange?: (
    errors: FieldErrors<InsuranceVerificationFormFields>,
    warnings: string[],
  ) => void;
  onAppliedRulesChange?: (rules: AppliedRuleDisplay[]) => void;
}

export default function VerificationForm({
  verification,
  onPredictedOutcomeChange,
  onFormDataChange,
  onValidationChange,
  onAppliedRulesChange,
}: VerificationFormProps) {
  const queryClient = useQueryClient();
  const [suggestedFields, setSuggestedFields] = useState<Set<string>>(
    new Set(),
  );
  const [suggestionNotes, setSuggestionNotes] = useState<
    Record<string, string>
  >({});
  const hasFetchedSuggestionsRef = useRef(false);
  const [fieldDependencyManager, setFieldDependencyManager] =
    useState<FieldDependencyManager | null>(null);
  const [ruleValues, setRuleValues] = useState<Record<string, FieldValue>>({});
  const [ruleConditions, setRuleConditions] = useState<
    Record<string, RuleCondition>
  >({});
  const [advisoryFields, setAdvisoryFields] = useState<string[]>([]);

  const handleDateChange = (date: Dayjs | null): string | undefined => {
    if (!date) return undefined;
    if (!date.isValid()) return undefined;
    try {
      return date.format("YYYY-MM-DD");
    } catch (_error) {
      return undefined;
    }
  };

  const wrapOnChangeForNumberField =
    (onChange: (...event: unknown[]) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value === "" ? null : Number(e.target.value);
      onChange(val);
    };

  const clearSuggestion = useCallback((fieldName: string) => {
    setSuggestedFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  }, []);

  const { control, setValue, getValues, trigger, formState } =
    useForm<InsuranceVerificationFormFields>({
      mode: "onChange",
      reValidateMode: "onChange",
      delayError: 1000,
      resolver: zodResolver(insuranceVerificationFormSchema),
      defaultValues: (() => {
        const baseDefaults =
          InsuranceVerificationDataService.transformForFormDisplay(
            verification,
          );

        return {
          ...baseDefaults,
          hasOrthodonticCoverage:
            baseDefaults.hasOrthodonticCoverage || undefined,
        };
      })(),
    });

  const watchedValues = useWatch({ control });

  const isCoverageTerminated = useMemo(
    () =>
      ConditionalDisplayManager.isCoverageTerminated(
        new Date(verification.appointmentDatetime),
        new Date(watchedValues.terminationDate),
      ),
    [watchedValues.terminationDate, verification.appointmentDatetime],
  );

  const getPredictedOutcome = useCallback(() => {
    if (isCoverageTerminated) {
      return "COVERAGE_INACTIVE";
    }

    if (watchedValues.hasOrthodonticCoverage === "NO") {
      return "COVERAGE_ACTIVE_NO_ORTHO";
    }

    if (
      watchedValues.orthoAmountRemaining !== null &&
      watchedValues.orthoAmountRemaining !== undefined &&
      watchedValues.orthoAmountRemaining <= 0
    ) {
      return "COVERAGE_ACTIVE_NO_ORTHO";
    }

    if (verification.patientDob && watchedValues.orthoAgeLimit) {
      try {
        const patientDob = new Date(verification.patientDob);
        const patientAge = Math.floor(
          (Date.now() - patientDob.getTime()) / (365.25 * 24 * 60 * 60 * 1000),
        );
        const ageLimit = Number(watchedValues.orthoAgeLimit);

        if (patientAge >= ageLimit) {
          return "COVERAGE_ACTIVE_NO_ORTHO";
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.warn("Invalid date calculation:", error);
      }
    }

    if (
      verification.appointmentDatetime &&
      watchedValues.orthoDateOfWaitingPeriod
    ) {
      try {
        const appointmentDate = new Date(verification.appointmentDatetime);
        const waitingPeriodEnd = new Date(
          watchedValues.orthoDateOfWaitingPeriod,
        );

        if (waitingPeriodEnd > appointmentDate) {
          return "COVERAGE_ACTIVE_NO_ORTHO";
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.warn("Invalid date calculation:", error);
      }
    }

    return "COVERAGE_ACTIVE_HAS_ORTHO";
  }, [
    isCoverageTerminated,
    watchedValues.hasOrthodonticCoverage,
    watchedValues.orthoAmountRemaining,
    watchedValues.orthoAgeLimit,
    watchedValues.orthoDateOfWaitingPeriod,
    verification.patientDob,
    verification.appointmentDatetime,
  ]);

  const formData = useMemo(
    () =>
      InsuranceVerificationDataService.prepareConditionalDisplayData(
        watchedValues,
        verification.appointmentDatetime,
      ),
    [watchedValues, verification.appointmentDatetime],
  );

  const fieldVisibility = useMemo(
    () => ConditionalDisplayManager.getAllFieldsVisibility(formData),
    [formData],
  );

  // Rules on fields the form currently hides or disables are moot for completion.
  const isRuleFieldActionable = useCallback(
    (fieldName: string) => {
      const visibility = (
        fieldVisibility as Record<
          string,
          { isVisible: boolean; isEditable: boolean } | undefined
        >
      )[ruleFieldVisibilityKey(fieldName)];
      return !visibility || (visibility.isVisible && visibility.isEditable);
    },
    [fieldVisibility],
  );

  const activeRuleValues = useMemo(() => {
    const values: Record<string, FieldValue> = { ...ruleValues };
    if (fieldDependencyManager) {
      Object.assign(
        values,
        fieldDependencyManager.getSuggestions(
          watchedValues as Record<string, FieldValue>,
        ),
      );
    }
    Object.keys(values).forEach((fieldName) => {
      if (!isRuleFieldActionable(fieldName)) {
        delete values[fieldName];
      }
    });
    return values;
  }, [
    ruleValues,
    fieldDependencyManager,
    watchedValues,
    isRuleFieldActionable,
  ]);

  const activeRuleConditions = useMemo(() => {
    const conditions: Record<string, RuleCondition> = { ...ruleConditions };
    if (fieldDependencyManager) {
      fieldDependencyManager
        .getMatchingRules(watchedValues as Record<string, FieldValue>)
        .forEach((rule) => {
          if (rule.conditions?.length) {
            conditions[rule.field] = { conditions: rule.conditions };
          }
        });
    }
    return conditions;
  }, [ruleConditions, fieldDependencyManager, watchedValues]);

  const overriddenRuleFields = useMemo(() => {
    const overridden: Record<string, FieldValue> = {};
    Object.entries(activeRuleValues).forEach(([fieldName, ruleValue]) => {
      if (ruleValue === null || ruleValue === undefined) return;
      const current = (watchedValues as Record<string, FieldValue | undefined>)[
        fieldName
      ];
      if (current === null || current === undefined || current === "") return;
      if (!ruleValueMatches(current, ruleValue)) {
        overridden[fieldName] = ruleValue;
      }
    });
    return overridden;
  }, [activeRuleValues, watchedValues]);

  const appliedRules = useMemo(() => {
    const rows: AppliedRuleDisplay[] = Object.entries(activeRuleValues).map(
      ([fieldName, value]) => ({
        field: fieldName,
        label: ruleFieldLabel(fieldName),
        value,
        note: suggestionNotes[fieldName] ?? null,
        status: fieldName in overriddenRuleFields ? "overridden" : "applied",
        condition: formatRuleCondition(activeRuleConditions[fieldName]),
      }),
    );
    advisoryFields.forEach((fieldName) => {
      if (
        !(fieldName in activeRuleValues) &&
        isRuleFieldActionable(fieldName)
      ) {
        rows.push({
          field: fieldName,
          label: ruleFieldLabel(fieldName),
          value: null,
          note: suggestionNotes[fieldName] ?? null,
          status: "advisory",
          condition: formatRuleCondition(activeRuleConditions[fieldName]),
        });
      }
    });
    return rows.sort((a, b) => a.label.localeCompare(b.label));
  }, [
    activeRuleValues,
    activeRuleConditions,
    overriddenRuleFields,
    advisoryFields,
    suggestionNotes,
    isRuleFieldActionable,
  ]);

  const getSuggestionProps = useCallback(
    (fieldName: string) => {
      const overriddenValue = overriddenRuleFields[fieldName];
      if (overriddenValue !== undefined) {
        const ruleNote = suggestionNotes[fieldName];
        const title = `Differs from Benny's payer rule (suggested: ${formatRuleValue(overriddenValue)})${ruleNote ? `. ${ruleNote}` : ""}`;
        return {
          isSuggested: false,
          sx: {
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#ef6c00",
                borderWidth: "2px",
              },
            },
          },
          adornment: (
            <InputAdornment position="end">
              <Tooltip title={title}>
                <WarningAmberIcon
                  sx={{
                    color: "#ef6c00",
                    fontSize: 18,
                    mr: 1,
                  }}
                />
              </Tooltip>
            </InputAdornment>
          ),
        };
      }

      const isSuggested = suggestedFields.has(fieldName);
      const note = suggestionNotes[fieldName] || "Value suggested by Benny";
      return {
        isSuggested,
        sx: isSuggested
          ? {
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#1976d2",
                  borderWidth: "2px",
                },
              },
            }
          : undefined,
        adornment: isSuggested ? (
          <InputAdornment position="end">
            <Tooltip title={note}>
              <AutoAwesomeIcon
                sx={{
                  color: "primary.main",
                  fontSize: 18,
                  mr: 1,
                }}
              />
            </Tooltip>
          </InputAdornment>
        ) : null,
      };
    },
    [suggestedFields, suggestionNotes, overriddenRuleFields],
  );

  const getValidationWarnings = useMemo(() => {
    const warnings: string[] = [];

    const amountUsedEditable = fieldVisibility.ortho_amount_used?.isEditable;
    const maxBenefitEditable =
      fieldVisibility.ortho_lifetime_max_benefit?.isEditable;

    if (
      amountUsedEditable &&
      maxBenefitEditable &&
      watchedValues.orthoAmountUsed !== null &&
      watchedValues.orthoLifetimeMaxBenefit !== null &&
      watchedValues.orthoAmountUsed > watchedValues.orthoLifetimeMaxBenefit
    ) {
      warnings.push("Amount used exceeds lifetime maximum benefit");
    }

    if (
      maxBenefitEditable &&
      watchedValues.orthoLifetimeMaxBenefit !== null &&
      watchedValues.orthoLifetimeMaxBenefit > 4000
    ) {
      warnings.push(
        "Lifetime Maximum benefit is greater than $4000. This is higher than the usual limit for ortho coverage, please double check.",
      );
    }

    Object.entries(overriddenRuleFields).forEach(([fieldName, ruleValue]) => {
      warnings.push(
        `${ruleFieldLabel(fieldName)} does not match Benny's payer rule (suggested: ${formatRuleValue(ruleValue)})`,
      );
    });

    return warnings;
  }, [watchedValues, fieldVisibility, overriddenRuleFields]);

  const getCurrentFormData = useCallback(
    (): InsuranceVerificationDetailUpdate => ({
      hasOrthodonticCoverage: watchedValues.hasOrthodonticCoverage,
      orthoManualOrAutomaticPayments:
        watchedValues.orthoManualOrAutomaticPayments,
      terminationDate: watchedValues.terminationDate,
      effectiveDate: watchedValues.effectiveDate,
      orthoPaymentFrequency: watchedValues.orthoPaymentFrequency,
      orthoLifetimeMaxBenefit: watchedValues.orthoLifetimeMaxBenefit,
      orthoInitialPaymentPercentage:
        watchedValues.orthoInitialPaymentPercentage,
      orthoAmountUsed: watchedValues.orthoAmountUsed,
      hasOrthoPlanDeductible: FieldConverter.stringToBoolean(
        watchedValues.hasOrthoPlanDeductible,
      ),
      orthoAmountRemaining: watchedValues.orthoAmountRemaining,
      orthoDeductibleRemaining: watchedValues.orthoDeductibleRemaining,
      orthoPercentagePlanPays: watchedValues.orthoPercentagePlanPays,
      hasOrthoWaitingPeriod: FieldConverter.stringToBoolean(
        watchedValues.hasOrthoWaitingPeriod,
      ),
      orthoAgeLimit: watchedValues.orthoAgeLimit,
      orthoDateOfWaitingPeriod: watchedValues.orthoDateOfWaitingPeriod,
      coordinationOfBenefits: watchedValues.coordinationOfBenefits,
      notes: watchedValues.notes,
      preAuthorizationRequired: watchedValues.preAuthorizationRequired,
      groupAcceptsAssignmentOfBenefits:
        watchedValues.groupAcceptsAssignmentOfBenefits,
      callReferenceNumber: watchedValues.callReferenceNumber,
    }),
    [watchedValues],
  );

  const fetchBennySuggestions = useCallback(
    async (insuranceKey: string, patientKey: string) => {
      if (hasFetchedSuggestionsRef.current) {
        return;
      }

      hasFetchedSuggestionsRef.current = true;

      try {
        const response = await queryClient.getBennySuggestions(
          insuranceKey,
          patientKey,
        );
        const suggestedFieldNames = new Set<string>();
        const notes: Record<string, string> = {};
        const currentValues = getValues();

        if (
          response.fieldDependencyRules &&
          response.fieldDependencyRules.length > 0
        ) {
          setFieldDependencyManager(
            new FieldDependencyManager(response.fieldDependencyRules),
          );
          response.fieldDependencyRules.forEach((rule) => {
            if (rule.note) {
              notes[rule.field] = rule.note;
            }
          });
        }

        if (response.suggestionNotes) {
          Object.assign(notes, response.suggestionNotes);
        }

        if (response.suggestions) {
          setRuleValues(response.suggestions);
          Object.entries(response.suggestions).forEach(([fieldName, value]) => {
            const currentValue =
              currentValues[fieldName as keyof InsuranceVerificationFormFields];

            if (
              currentValue === null ||
              currentValue === undefined ||
              currentValue === ""
            ) {
              const formValue =
                typeof value === "number" ? String(value) : value;
              setValue(
                fieldName as keyof InsuranceVerificationFormFields,
                formValue,
                { shouldValidate: true },
              );
              suggestedFieldNames.add(fieldName);
            }
          });
        }

        // Advisory-only fields: in suggestionNotes but not suggestions, so show the
        // Benny hint without a prefill. fieldDependencyRules notes aren't advisories.
        const advisoryOnlyFieldNames = Object.keys(
          response.suggestionNotes ?? {},
        ).filter(
          (fieldName) =>
            !response.suggestions || !(fieldName in response.suggestions),
        );
        advisoryOnlyFieldNames.forEach((fieldName) => {
          suggestedFieldNames.add(fieldName);
        });
        setAdvisoryFields(advisoryOnlyFieldNames);
        setRuleConditions(
          (response.suggestionConditions as Record<string, RuleCondition>) ??
            {},
        );

        setSuggestedFields(suggestedFieldNames);
        setSuggestionNotes(notes);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to fetch Benny suggestions:", error);
      }
    },
    [queryClient, getValues, setValue],
  );

  useEffect(() => {
    if (
      verification.wieldyId &&
      verification.insuranceKey &&
      verification.patientKey &&
      (verification.verificationStatus === "QUEUED" ||
        verification.verificationStatus === "REVIEW")
    ) {
      fetchBennySuggestions(verification.insuranceKey, verification.patientKey);
    }
  }, [
    verification.wieldyId,
    verification.insuranceKey,
    verification.patientKey,
    verification.verificationStatus,
    fetchBennySuggestions,
  ]);

  // Rules are payer-configured, so re-evaluate whenever any field they name changes.
  const liveDependencySignature = useMemo(() => {
    if (!fieldDependencyManager) return "";
    const values = watchedValues as Record<string, unknown>;
    return fieldDependencyManager
      .getDependencyFields()
      .map((field) => `${field}=${JSON.stringify(values[field] ?? null)}`)
      .join("|");
  }, [fieldDependencyManager, watchedValues]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping liveDependencySignature would change when this runs
  useEffect(() => {
    if (!fieldDependencyManager) return;

    const currentValues = getValues();
    const fieldSuggestions =
      fieldDependencyManager.getSuggestions(currentValues);
    const markSuggested = (fieldName: string) =>
      setSuggestedFields((prev) =>
        prev.has(fieldName) ? prev : new Set(prev).add(fieldName),
      );

    Object.entries(fieldSuggestions).forEach(([fieldName, suggestedValue]) => {
      const currentValue =
        currentValues[fieldName as keyof InsuranceVerificationFormFields];

      if (
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
      ) {
        setValue(
          fieldName as keyof InsuranceVerificationFormFields,
          suggestedValue,
          { shouldValidate: true },
        );
        markSuggested(fieldName);
      } else if (currentValue === suggestedValue) {
        markSuggested(fieldName);
      }
    });
  }, [liveDependencySignature, fieldDependencyManager, getValues, setValue]);

  useEffect(() => {
    const maxBenefit = watchedValues.orthoLifetimeMaxBenefit;
    const amountUsed = watchedValues.orthoAmountUsed;

    if (
      maxBenefit !== null &&
      amountUsed !== null &&
      maxBenefit >= 0 &&
      amountUsed >= 0
    ) {
      const calculationResult =
        AutoCalculationManager.calculateOrthoAmountRemaining(
          maxBenefit,
          amountUsed,
        );

      if (
        calculationResult.isValid &&
        calculationResult.calculatedValue !== watchedValues.orthoAmountRemaining
      ) {
        setValue("orthoAmountRemaining", calculationResult.calculatedValue, {
          shouldValidate: true,
        });
      }
    }
  }, [
    watchedValues.orthoLifetimeMaxBenefit,
    watchedValues.orthoAmountUsed,
    setValue,
    watchedValues.orthoAmountRemaining,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping watchedValues.orthoLifetimeMaxBenefit would change when this runs
  useEffect(() => {
    trigger("orthoAmountUsed");
  }, [watchedValues.orthoLifetimeMaxBenefit, trigger]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping watchedValues.orthoAmountUsed would change when this runs
  useEffect(() => {
    trigger("orthoLifetimeMaxBenefit");
  }, [watchedValues.orthoAmountUsed, trigger]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping watchedValues.terminationDate would change when this runs
  useEffect(() => {
    const dependentFields = [
      "effectiveDate",
      "hasOrthodonticCoverage",
      "orthoLifetimeMaxBenefit",
      "orthoAmountUsed",
      "orthoAmountRemaining",
      "orthoPercentagePlanPays",
      "orthoManualOrAutomaticPayments",
      "orthoPaymentFrequency",
      "orthoInitialPaymentPercentage",
      "hasOrthoPlanDeductible",
      "orthoDeductibleRemaining",
      "hasOrthoWaitingPeriod",
      "orthoDateOfWaitingPeriod",
      "coordinationOfBenefits",
    ];

    dependentFields.forEach((field) => {
      trigger(field as keyof InsuranceVerificationFormFields);
    });
  }, [watchedValues.terminationDate, trigger]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping watchedValues.hasOrthodonticCoverage would change when this runs
  useEffect(() => {
    const orthoDependentFields = [
      "orthoLifetimeMaxBenefit",
      "orthoAmountUsed",
      "orthoAmountRemaining",
      "orthoPercentagePlanPays",
      "orthoAgeLimit",
      "orthoManualOrAutomaticPayments",
      "orthoPaymentFrequency",
      "orthoInitialPaymentPercentage",
      "hasOrthoPlanDeductible",
      "orthoDeductibleRemaining",
      "hasOrthoWaitingPeriod",
      "orthoDateOfWaitingPeriod",
      "coordinationOfBenefits",
    ];

    orthoDependentFields.forEach((field) => {
      trigger(field as keyof InsuranceVerificationFormFields);
    });
  }, [watchedValues.hasOrthodonticCoverage, trigger]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping isCoverageTerminated, watchedValues.hasOrthodonticCoverage, watchedValues.orthoAmountRemaining, watchedValues.orthoAgeLimit, watchedValues.orthoDateOfWaitingPeriod would change when this runs
  useEffect(() => {
    if (onPredictedOutcomeChange) {
      const outcome = getPredictedOutcome();
      onPredictedOutcomeChange(outcome);
    }
  }, [
    isCoverageTerminated,
    watchedValues.hasOrthodonticCoverage,
    watchedValues.orthoAmountRemaining,
    watchedValues.orthoAgeLimit,
    watchedValues.orthoDateOfWaitingPeriod,
    onPredictedOutcomeChange,
    getPredictedOutcome,
  ]);

  useEffect(() => {
    if (onFormDataChange) {
      const currentData = getCurrentFormData();
      onFormDataChange(currentData);
    }
  }, [getCurrentFormData, onFormDataChange]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(formState.errors, getValidationWarnings);
    }
  }, [onValidationChange, formState.errors, getValidationWarnings]);

  useEffect(() => {
    onAppliedRulesChange?.(appliedRules);
  }, [onAppliedRulesChange, appliedRules]);

  useEffect(() => () => onAppliedRulesChange?.([]), [onAppliedRulesChange]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <AssignmentIcon color="primary" />
            <Typography variant="h6">Verification Details</Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Complete the verification details below. The system will
            automatically determine the final verification outcome based on the
            information provided.
          </Alert>

          <Grid container spacing={3}>
            {/* Coverage Dates Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Coverage Information
              </Typography>
            </Grid>

            {fieldVisibility.termination_date?.isVisible && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="terminationDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DesktopDatePicker
                      label="Termination Date"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date: Dayjs | null) =>
                        field.onChange(handleDateChange(date))
                      }
                      disabled={!fieldVisibility.termination_date?.isEditable}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!fieldState.error,
                          helperText:
                            fieldState.error?.message ||
                            "Leave blank if coverage is active",
                          inputProps: {
                            "data-testid": "terminationDate",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {fieldVisibility.effective_date?.isVisible && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="effectiveDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DesktopDatePicker
                      label="Effective Date"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date: Dayjs | null) =>
                        field.onChange(handleDateChange(date))
                      }
                      disabled={!fieldVisibility.effective_date?.isEditable}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                          inputProps: {
                            "data-testid": "effectiveDate",
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            )}

            {!isCoverageTerminated && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Orthodontic Benefits
                  </Typography>
                </Grid>

                {(fieldVisibility.has_orthodontic_coverage?.isVisible ??
                  true) && (
                  <Grid item xs={12}>
                    <Controller
                      name="hasOrthodonticCoverage"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl
                          component="fieldset"
                          error={!!fieldState.error}
                        >
                          <FormLabel
                            component="legend"
                            sx={{ fontWeight: 600 }}
                          >
                            Does the dental policy cover orthodontics? *
                          </FormLabel>
                          <RadioGroup
                            value={field.value || ""}
                            onChange={field.onChange}
                            row
                            sx={{ mt: 1 }}
                          >
                            <FormControlLabel
                              value="YES"
                              control={<Radio />}
                              label="Yes"
                              disabled={
                                !(
                                  fieldVisibility.has_orthodontic_coverage
                                    ?.isEditable ?? true
                                )
                              }
                              data-testid="hasOrthodonticCoverageYes"
                            />
                            <FormControlLabel
                              value="NO"
                              control={<Radio />}
                              label="No"
                              disabled={
                                !(
                                  fieldVisibility.has_orthodontic_coverage
                                    ?.isEditable ?? true
                                )
                              }
                              data-testid="hasOrthodonticCoverageNo"
                            />
                          </RadioGroup>
                          {fieldState.error && (
                            <ErrorHelperText>
                              {fieldState.error.message}
                            </ErrorHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}

                {/* Pre-Authorization Required - conditionally visible after ortho coverage */}
                {fieldVisibility.pre_authorization_required?.isVisible && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="preAuthorizationRequired"
                        control={control}
                        render={({ field, fieldState }) => {
                          const suggestion = getSuggestionProps(
                            "preAuthorizationRequired",
                          );
                          return (
                            <Tooltip title="Some insurance plans require pre-authorization before treatment begins. Leave blank if you are unsure.">
                              <TextField
                                fullWidth
                                select
                                label="Does the dental policy require pre-authorization?"
                                value={field.value || ""}
                                onChange={(e) => {
                                  if (suggestion.isSuggested) {
                                    clearSuggestion("preAuthorizationRequired");
                                  }
                                  field.onChange(e);
                                }}
                                disabled={
                                  !(
                                    fieldVisibility.pre_authorization_required
                                      ?.isEditable ?? true
                                  )
                                }
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                data-testid="preAuthorizationRequired"
                                sx={suggestion.sx}
                                InputProps={{
                                  endAdornment: suggestion.adornment,
                                  inputProps: {
                                    "aria-label":
                                      "Does the dental policy require pre-authorization?",
                                    "aria-describedby":
                                      "preAuthorizationRequired-description",
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select an option</em>
                                </MenuItem>
                                <MenuItem value="YES">Yes</MenuItem>
                                <MenuItem value="NO">No</MenuItem>
                              </TextField>
                            </Tooltip>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={false} sm={6} />
                  </>
                )}

                {/* Orthodontic Fields - conditionally visible */}
                {fieldVisibility.ortho_lifetime_max_benefit?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoLifetimeMaxBenefit"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          fullWidth
                          label="Lifetime Maximum Benefit *"
                          type="number"
                          value={field.value || ""}
                          onChange={wrapOnChangeForNumberField(field.onChange)}
                          disabled={
                            !fieldVisibility.ortho_lifetime_max_benefit
                              ?.isEditable
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          data-testid="orthoLifetimeMaxBenefit"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_amount_used?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoAmountUsed"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          fullWidth
                          label="Amount Used *"
                          type="number"
                          value={
                            field.value !== null && field.value !== undefined
                              ? field.value
                              : ""
                          }
                          onChange={wrapOnChangeForNumberField(field.onChange)}
                          disabled={
                            !fieldVisibility.ortho_amount_used?.isEditable
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          data-testid="orthoAmountUsed"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_amount_remaining?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoAmountRemaining"
                      control={control}
                      render={({ field, fieldState }) => (
                        <Tooltip
                          title={
                            fieldVisibility.ortho_amount_remaining?.isCalculated
                              ? "This field is automatically calculated"
                              : ""
                          }
                        >
                          <TextField
                            fullWidth
                            label="Amount Remaining *"
                            type="number"
                            value={field.value || ""}
                            onChange={wrapOnChangeForNumberField(
                              field.onChange,
                            )}
                            disabled={
                              !fieldVisibility.ortho_amount_remaining
                                ?.isEditable
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Tooltip>
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_percentage_plan_pays?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoPercentagePlanPays"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          fullWidth
                          label="Percentage Plan Pays *"
                          type="number"
                          value={field.value || ""}
                          onChange={wrapOnChangeForNumberField(field.onChange)}
                          disabled={
                            !fieldVisibility.ortho_percentage_plan_pays
                              ?.isEditable
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          data-testid="orthoPercentagePlanPays"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_age_limit?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoAgeLimit"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          fullWidth
                          label="Ortho Age Limit *"
                          type="number"
                          value={
                            field.value !== null && field.value !== undefined
                              ? field.value
                              : ""
                          }
                          onChange={wrapOnChangeForNumberField(field.onChange)}
                          disabled={
                            !fieldVisibility.ortho_age_limit?.isEditable
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          data-testid="orthoAgeLimit"
                        />
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_initial_payment_percentage
                  ?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoInitialPaymentPercentage"
                      control={control}
                      render={({ field, fieldState }) => {
                        const suggestion = getSuggestionProps(
                          "orthoInitialPaymentPercentage",
                        );
                        return (
                          <TextField
                            fullWidth
                            label="Initial Payment Percentage"
                            type="number"
                            value={field.value || ""}
                            onChange={(e) => {
                              if (suggestion.isSuggested) {
                                clearSuggestion(
                                  "orthoInitialPaymentPercentage",
                                );
                              }
                              wrapOnChangeForNumberField(field.onChange)(
                                e as React.ChangeEvent<HTMLInputElement>,
                              );
                            }}
                            disabled={
                              !fieldVisibility.ortho_initial_payment_percentage
                                ?.isEditable
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            data-testid="orthoInitialPaymentPercentage"
                            sx={suggestion.sx}
                            InputProps={{
                              endAdornment: (
                                <>
                                  {suggestion.adornment}
                                  <InputAdornment position="end">
                                    %
                                  </InputAdornment>
                                </>
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_manual_or_automatic_payments
                  ?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoManualOrAutomaticPayments"
                      control={control}
                      render={({ field, fieldState }) => {
                        const suggestion = getSuggestionProps(
                          "orthoManualOrAutomaticPayments",
                        );
                        return (
                          <TextField
                            select
                            fullWidth
                            label="Manual or Automatic Payments"
                            value={field.value || ""}
                            onChange={(e) => {
                              if (suggestion.isSuggested) {
                                clearSuggestion(
                                  "orthoManualOrAutomaticPayments",
                                );
                              }
                              field.onChange(e);
                            }}
                            disabled={
                              !fieldVisibility
                                .ortho_manual_or_automatic_payments?.isEditable
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            data-testid="orthoManualOrAutomaticPayments"
                            sx={suggestion.sx}
                            InputProps={{
                              endAdornment: suggestion.adornment,
                            }}
                          >
                            <MenuItem value="MANUAL">Manual</MenuItem>
                            <MenuItem value="AUTOMATIC">Automatic</MenuItem>
                          </TextField>
                        );
                      }}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_payment_frequency?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoPaymentFrequency"
                      control={control}
                      render={({ field, fieldState }) => {
                        const suggestion = getSuggestionProps(
                          "orthoPaymentFrequency",
                        );
                        return (
                          <TextField
                            select
                            fullWidth
                            label="Payment Frequency"
                            value={field.value || ""}
                            onChange={(e) => {
                              if (suggestion.isSuggested) {
                                clearSuggestion("orthoPaymentFrequency");
                              }
                              field.onChange(e);
                            }}
                            disabled={
                              !fieldVisibility.ortho_payment_frequency
                                ?.isEditable
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            data-testid="orthoPaymentFrequency"
                            sx={suggestion.sx}
                            InputProps={{
                              endAdornment: suggestion.adornment,
                            }}
                          >
                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                            <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                            <MenuItem value="SEMI_ANNUAL">Semi-Annual</MenuItem>
                            <MenuItem value="YEARLY">Yearly</MenuItem>
                          </TextField>
                        );
                      }}
                    />
                  </Grid>
                )}

                {fieldVisibility.has_ortho_deductible_plan?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="hasOrthoPlanDeductible"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl
                          component="fieldset"
                          error={!!fieldState.error}
                        >
                          <FormLabel component="legend">
                            Deductible Applied
                          </FormLabel>
                          <RadioGroup
                            value={field.value || ""}
                            onChange={field.onChange}
                            row
                          >
                            <FormControlLabel
                              value="YES"
                              control={<Radio />}
                              label="Yes"
                              disabled={
                                !fieldVisibility.has_ortho_deductible_plan
                                  ?.isEditable
                              }
                              data-testid="hasOrthoPlanDeductibleYes"
                            />
                            <FormControlLabel
                              value="NO"
                              control={<Radio />}
                              label="No"
                              disabled={
                                !fieldVisibility.has_ortho_deductible_plan
                                  ?.isEditable
                              }
                              data-testid="hasOrthoPlanDeductibleNo"
                            />
                          </RadioGroup>
                          {fieldState.error && (
                            <ErrorHelperText>
                              {fieldState.error.message}
                            </ErrorHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_deductible_remaining?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoDeductibleRemaining"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          fullWidth
                          label="Deductible Remaining"
                          type="number"
                          value={field.value || ""}
                          onChange={wrapOnChangeForNumberField(field.onChange)}
                          disabled={
                            !fieldVisibility.ortho_deductible_remaining
                              ?.isEditable
                          }
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          data-testid="orthoDeductibleRemaining"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.has_ortho_waiting_period?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="hasOrthoWaitingPeriod"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl
                          component="fieldset"
                          error={!!fieldState.error}
                        >
                          <FormLabel component="legend">
                            Waiting Period
                          </FormLabel>
                          <RadioGroup
                            value={field.value || ""}
                            onChange={field.onChange}
                            row
                          >
                            <FormControlLabel
                              value="YES"
                              control={<Radio />}
                              label="Yes"
                              disabled={
                                !fieldVisibility.has_ortho_waiting_period
                                  ?.isEditable
                              }
                              data-testid="hasOrthoWaitingPeriodYes"
                            />
                            <FormControlLabel
                              value="NO"
                              control={<Radio />}
                              label="No"
                              disabled={
                                !fieldVisibility.has_ortho_waiting_period
                                  ?.isEditable
                              }
                              data-testid="hasOrthoWaitingPeriodNo"
                            />
                          </RadioGroup>
                          {fieldState.error && (
                            <ErrorHelperText>
                              {fieldState.error.message}
                            </ErrorHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}

                {fieldVisibility.ortho_waiting_period_date?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="orthoDateOfWaitingPeriod"
                      control={control}
                      render={({ field, fieldState }) => (
                        <DesktopDatePicker
                          label="Waiting Period End Date"
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date: Dayjs | null) =>
                            field.onChange(handleDateChange(date))
                          }
                          disabled={
                            !fieldVisibility.ortho_waiting_period_date
                              ?.isEditable
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                              inputProps: {
                                "data-testid": "orthoWaitingPeriodDate",
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Assignment of Benefits */}
                {fieldVisibility.group_accepts_assignment_of_benefits
                  ?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="groupAcceptsAssignmentOfBenefits"
                      control={control}
                      render={({ field, fieldState }) => {
                        const suggestion = getSuggestionProps(
                          "groupAcceptsAssignmentOfBenefits",
                        );
                        return (
                          <Tooltip title="Indicates whether the insurance group allows benefits to be paid directly to the provider. Leave blank if you are unsure.">
                            <TextField
                              fullWidth
                              select
                              label="Assignment of Benefits"
                              value={field.value || ""}
                              onChange={(e) => {
                                if (suggestion.isSuggested) {
                                  clearSuggestion(
                                    "groupAcceptsAssignmentOfBenefits",
                                  );
                                }
                                field.onChange(e);
                              }}
                              disabled={
                                !(
                                  fieldVisibility
                                    .group_accepts_assignment_of_benefits
                                    ?.isEditable ?? true
                                )
                              }
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              data-testid="groupAcceptsAssignmentOfBenefits"
                              sx={suggestion.sx}
                              InputProps={{
                                endAdornment: suggestion.adornment,
                                inputProps: {
                                  "aria-label": "Assignment of Benefits",
                                  "aria-describedby":
                                    "groupAcceptsAssignmentOfBenefits-description",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select an option</em>
                              </MenuItem>
                              <MenuItem value="YES">Yes</MenuItem>
                              <MenuItem value="NO">No</MenuItem>
                            </TextField>
                          </Tooltip>
                        );
                      }}
                    />
                  </Grid>
                )}

                {/* Coordination of Benefits */}
                {fieldVisibility.coordination_of_benefits?.isVisible && (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="coordinationOfBenefits"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          fullWidth
                          label="Coordination of Benefits"
                          value={field.value || ""}
                          onChange={field.onChange}
                          disabled={
                            !fieldVisibility.coordination_of_benefits
                              ?.isEditable
                          }
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error?.message || "e.g., Standard, COB"
                          }
                          data-testid="coordinationOfBenefits"
                          placeholder="e.g., Standard, COB"
                        />
                      )}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* Notes Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Notes
              </Typography>
            </Grid>

            {/* Call Reference Number */}
            <Grid item xs={12}>
              <Controller
                name="callReferenceNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <Tooltip title="Reference number provided by insurance carrier during verification call.">
                    <TextField
                      fullWidth
                      label="Call Reference Number"
                      value={field.value || ""}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        `${(field.value || "").length}/255 characters`
                      }
                      data-testid="callReferenceNumber"
                      placeholder="e.g., REF123456789"
                      inputProps={{
                        "aria-label": "Call Reference Number",
                        "aria-describedby": "callReferenceNumber-description",
                        maxLength: 255,
                      }}
                    />
                  </Tooltip>
                )}
              />
            </Grid>

            {fieldVisibility.notes?.isVisible && (
              <Grid item xs={12}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      fullWidth
                      label="Notes"
                      value={field.value || ""}
                      onChange={field.onChange}
                      disabled={!fieldVisibility.notes?.isEditable}
                      error={!!fieldState.error}
                      helperText={`${fieldState.error?.message || ""} ${verification.verificationStatusReason || ""}`.trim()}
                      data-testid="notes"
                      multiline
                      rows={2}
                      placeholder="Add any additional notes about this verification..."
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </LocalizationProvider>
  );
}
